"use client";

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Image from "next/image";

import { DateTime } from "luxon";
import { GrEdit, GrGallery } from "react-icons/gr";
import {
    LuDownload,
    LuGlobe,
    LuLoader2,
    LuLock,
    LuPencil,
    LuX,
} from "react-icons/lu";
import { EventJourneyStatus } from "@prisma/client";
import { useSession } from "next-auth/react";
import { zodResolver } from "@hookform/resolvers/zod";

import { usePermissionsContext } from "~/app/providers";
import { cn } from "~/utils/fontsConfig";
import { toTitleCase } from "~/utils/string";
import { getImagePath } from "~/utils/images";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/shadcn/Select";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "~/components/v3/shadcn/Form";
import {
    Sheet,
    SheetClose,
    SheetContent,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from "~/components/v3/shadcn/Sheet";
import { Button } from "~/components/v3/themed/Button";
import { Input } from "~/components/v4/shadcn/Input";
import { Label } from "~/components/v3/shadcn/Label";
import { TextArea } from "~/components/v4/shadcn/TextArea";
import { TimePicker } from "~/components/v3/shadcn/TimePicker";
import { DatePicker } from "~/components/v3/shadcn/DatePicker";
import {
    LocationSelector,
    Location,
} from "~/components/v3/shadcn/LocationSelector";
import { TimezoneSelect } from "~/components/v3/shadcn/Timezone";
import { TagsInput } from "~/components/v4/shadcn/TagsInput";
import { toastHandler } from "~/components/ToastHandler";
import { EventImageSelectionModal } from "~/components/v3/events-components/EventImage";
import { CapacityInput } from "~/components/v4/shadcn/CapacityInput";
import { Checkbox } from "~/components/shadcn/Checkbox";
import { If } from "~/components/v3/Conditional";
import { Switch } from "~/components/shadcn/Switch";
import UploadImage from "~/components/file/UploadImage";

import {
    editFormSchema,
    EditFormSchema,
    getDefaultFormValues,
} from "../utils/event-form";
import { getTimezoneFromLocation } from "../utils/timezone";
import { GetEventFromSlug, updateEvent } from "../actions";

import DescriptionTextPopup from "./DescrptionTextPopup";
import { EditEventURLs } from "./EditEventURLs";
import { CustomizableLayout } from "~/types/CustomizableLayout";

export const EditEventSheet = ({
    isOpen,
    setIsOpen,
    event,
    customization,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    event: GetEventFromSlug;
    customization?: CustomizableLayout;
}) => {
    const { data: session } = useSession();
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const permissions = usePermissionsContext();

    const isAllowedToEditStatus =
        permissions.isManager ||
        permissions.isAdmin ||
        event.user?.id === session?.user?.id; //TODO:@LemmyMwaura move this to the permissions

    const isAllowedToEditAdvancedProperties =
        permissions.isManager || permissions.isAdmin;

    const defaultValues = getDefaultFormValues(event);
    const form = useForm<EditFormSchema>({
        defaultValues,
        resolver: zodResolver(editFormSchema),
    });

    useEffect(() => {
        form.reset(defaultValues);
    }, [event]);

    const startDate = form.watch("startDate");
    const endDate = form.watch("endDate");
    const selectedImage = form.watch("image");
    const location = form.watch("location");
    const isLocationHidden = form.watch("isLocationHidden");
    const imagePath = getImagePath(selectedImage, event.id);

    const isPaidEvent =
        event.eventTickets.some((ticket) => ticket.price && ticket.price > 0) ||
        (event?.priceDollars !== null && event.priceDollars > 0);

    /** Handle cases when the end date is before the start date */
    const handleDateChange = () => {
        const startDateTime = DateTime.fromJSDate(startDate.date).set({
            hour: parseInt(startDate.time?.split(":")[0] ?? "0"),
            minute: parseInt(startDate.time?.split(":")[1] ?? "0"),
        });

        const endDateTime = DateTime.fromJSDate(endDate.date).set({
            hour: parseInt(endDate.time?.split(":")[0] ?? "0"),
            minute: parseInt(endDate.time?.split(":")[1] ?? "0"),
        });

        if (startDateTime > endDateTime) {
            form.setValue("endDate.date", startDate.date);
            const endTime = startDateTime.plus({ hours: 1 }).toFormat("HH:mm");
            form.setValue("endDate.time", endTime);
        }
    };

    const getStartDateTime = () => {
        return DateTime.fromJSDate(form.getValues("startDate.date"))
            .set({
                hour: parseInt(
                    form.getValues("startDate.time")?.split(":")[0] ?? "0",
                ),
                minute: parseInt(
                    form.getValues("startDate.time")?.split(":")[1] ?? "0",
                ),
            })
            .toJSDate();
    };

    const onSubmit = async (data: EditFormSchema) => {
        setIsLoading(true);
        toastHandler({
            loadingMessage: "Updating event...",
            successMessage: "Event updated successfully",
            errorMessage: "Failed to update event",
            operation: async () => {
                await updateEvent(event?.id, event?.slug, {
                    ...data,
                    startDate: {
                        date: DateTime.fromJSDate(data.startDate.date)
                            .setZone("UTC", { keepLocalTime: true })
                            .toJSDate(),
                        time: data.startDate.time,
                    },
                    endDate: {
                        date: DateTime.fromJSDate(data.endDate.date)
                            .setZone("UTC", { keepLocalTime: true })
                            .toJSDate(),
                        time: data.endDate.time,
                    },
                });
                setIsOpen(false);
            },
            onSettled: () => {
                setIsLoading(false);
                setIsOpen(false);
            },
        });
    };

    /** handle case when the location is removed by user */
    const handleLocationDelete = () => {
        form.setValue("isLocationHidden", false);
        form.setValue("timeout", 0);
        form.setValue("pseudoLocation", "");
    };

    const handleProtectedTimeoutChange = (value: string | number | null) => {
        const numValue = Number(value);
        form.setValue("timeout", Math.max(0, numValue) || 0);
    };

    const updateTimezoneFromLocation = async (location: Location) => {
        const matchedTimezone = await getTimezoneFromLocation(
            location,
            getStartDateTime(),
        );
        if (matchedTimezone) form.setValue("timezone", matchedTimezone);
    };

    const handleDownloadImage = async () => {
        if (!imagePath) return;

        try {
            const response = await fetch(imagePath);
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = `${event.slug}-${event.image}`;
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            console.error("Failed to download image:", error);
        }
    };

    const eventStatuses = [
        EventJourneyStatus.DRAFT,
        EventJourneyStatus.PUBLISHED,
        EventJourneyStatus.IGNORED,
        ...(event.community
            ? [EventJourneyStatus.PROPOSED, EventJourneyStatus.DRAFT_PROPOSED]
            : []),
    ];

    // if the user is not allowed to edit the status(not admin or manager or profile event owner) and the status is draft proposed or proposed, then we disable the status.
    const isStatusDisabled =
        !isAllowedToEditStatus &&
        (event.eventJourneyStatus === EventJourneyStatus.DRAFT_PROPOSED ||
            event.eventJourneyStatus === EventJourneyStatus.PROPOSED);

    return (
        <Sheet open={isOpen} onOpenChange={() => setIsOpen(false)}>
            <SheetContent
                showCloseIcon={false}
                className={cn(
                    "flex h-full w-full flex-col overflow-y-auto rounded-br-md rounded-tr-md border-none bg-v3-N600 p-4 md:min-w-[500px] md:p-6",
                    customization?.mainCardClassName,
                )}
            >
                <SheetHeader className="flex-none py-2">
                    <div className="flex w-full items-center justify-between">
                        <SheetTitle
                            className={cn(
                                "text-lg",
                                customization?.labelClassName,
                            )}
                        >
                            Edit Event
                        </SheetTitle>
                        <SheetClose className="focus:ring-ring rounded-sm opacity-90 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
                            <LuX className="h-5 w-5" />
                            <span className="sr-only">Close</span>
                        </SheetClose>
                    </div>
                </SheetHeader>

                <Form {...form}>
                    <form
                        className="flex flex-col gap-6"
                        onSubmit={form.handleSubmit(onSubmit)}
                    >
                        <div className="flex flex-col gap-2">
                            <div className="bg-v3-N700 relative aspect-video w-full rounded-md">
                                <Image
                                    width={50}
                                    height={50}
                                    className="aspect-video h-auto w-full rounded-[4px]"
                                    src={imagePath}
                                    priority
                                    sizes="(max-width: 768px) 100vw, 50vw"
                                    alt="event image"
                                />
                                <button
                                    type="button"
                                    onClick={handleDownloadImage}
                                    className={cn(
                                        "group absolute bottom-2 right-[6.5rem]",
                                        "z-50 cursor-pointer rounded-full bg-white/80 p-3",
                                        "hover:scale-[0.95] hover:bg-white hover:shadow-lg hover:shadow-black/20",
                                        "transition-all ease-in active:scale-95",
                                        {
                                            "cursor-not-allowed": isLoading,
                                        },
                                    )}
                                >
                                    <LuDownload className="text-md text-black" />
                                </button>

                                <UploadImage
                                    bucketKey={event.id}
                                    cropBeforeUpload={true}
                                    className={cn(
                                        "group absolute bottom-2 right-2",
                                        "h-10 w-10 hover:ring-0",
                                        "z-50 cursor-pointer rounded-full bg-white p-3",
                                        "hover:scale-[0.95] hover:shadow-lg hover:shadow-black/20",
                                        "transition-all ease-in active:scale-95",
                                        {
                                            "cursor-not-allowed": isLoading,
                                        },
                                    )}
                                    onUploadComplete={(filename) => {
                                        form.setValue("image", filename, {
                                            shouldDirty: true,
                                        });
                                    }}
                                    setIsUploading={setIsLoading}
                                    disabled={isLoading}
                                >
                                    <GrEdit className="text-md text-black" />
                                </UploadImage>

                                <button
                                    className={cn(
                                        "group absolute bottom-2 right-14",
                                        "z-50 cursor-pointer rounded-full bg-white/80 p-3",
                                        "hover:scale-[0.95] hover:bg-white hover:shadow-lg hover:shadow-black/20",
                                        "transition-all ease-in active:scale-95",
                                        {
                                            "cursor-not-allowed": isLoading,
                                        },
                                    )}
                                    type="button"
                                    onClick={(e) => setIsImageModalOpen(true)}
                                    disabled={isLoading}
                                >
                                    <GrGallery className="text-md text-black" />
                                </button>
                            </div>
                        </div>

                        <FormField
                            control={form.control}
                            name="eventName"
                            render={({ field }) => (
                                <FormItem className="flex w-full flex-col gap-1">
                                    <FormLabel
                                        className={cn(
                                            "text-sm font-[300] text-v3-N300",
                                            customization?.inputLabelClassName,
                                        )}
                                    >
                                        Name
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            maxLength={140}
                                            className={cn(
                                                "rounded-[0.625rem] tracking-wide",
                                                customization?.inputClassName,
                                            )}
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <DescriptionTextPopup
                                    value={field.value}
                                    customization={customization}
                                >
                                    <TextArea
                                        {...field}
                                        value={field.value ?? ""}
                                        className={cn(
                                            "min-h-40 w-full rounded-[0.625rem]",
                                            customization?.inputClassName,
                                        )}
                                    />
                                </DescriptionTextPopup>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="visibility"
                            render={({ field }) => (
                                <FormItem className="flex w-full flex-col gap-2">
                                    <FormLabel
                                        className={cn(
                                            "text-sm font-[300] text-v3-N300",
                                            customization?.inputLabelClassName,
                                        )}
                                    >
                                        Visibility
                                    </FormLabel>
                                    <FormControl>
                                        <Select
                                            onValueChange={field.onChange}
                                            value={field.value}
                                        >
                                            <SelectTrigger
                                                className={cn(
                                                    "rounded-[10px] p-2 px-3 focus:ring-0 focus:ring-offset-0",
                                                    "hover:border hover:border-v3-clover focus:border focus:border-v3-clover focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
                                                    customization?.inputClassName,
                                                )}
                                            >
                                                <SelectValue>
                                                    <div className="flex items-center gap-2">
                                                        <span
                                                            className="flex justify-center rounded-full bg-v3-clover/20 p-[6px]"
                                                            aria-label="Visibility"
                                                        >
                                                            {field.value ===
                                                            "Public" ? (
                                                                <LuGlobe className="text-lg text-v3-clover" />
                                                            ) : (
                                                                <LuLock className="text-lg text-v3-clover" />
                                                            )}
                                                        </span>
                                                        <span>
                                                            {field.value}
                                                        </span>
                                                    </div>
                                                </SelectValue>
                                            </SelectTrigger>

                                            <SelectContent
                                                position="item-aligned"
                                                className={
                                                    customization?.selectInput
                                                        ?.listClassName
                                                }
                                            >
                                                <SelectItem value="Public">
                                                    Public
                                                </SelectItem>
                                                <SelectItem value="Private">
                                                    Private
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        ></FormField>

                        <FormField
                            control={form.control}
                            name="isPreapproved"
                            render={({ field }) => (
                                <FormItem className="flex w-full flex-col gap-2">
                                    <div className="flex items-center gap-4">
                                        <FormControl>
                                            <Checkbox
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                                disabled={isPaidEvent}
                                                theme={
                                                    customization?.defaultTheme
                                                }
                                            />
                                        </FormControl>
                                        <div className="flex flex-col gap-1">
                                            <FormLabel
                                                className={cn(
                                                    "text-sm font-[300] text-v3-N300",
                                                    customization?.inputLabelClassName,
                                                )}
                                            >
                                                Attendee Approval
                                            </FormLabel>
                                            <p
                                                className={cn(
                                                    "text-sm text-v3-N400",
                                                    customization?.descriptionClassName,
                                                )}
                                            >
                                                Attendees must be approved by
                                                the host.
                                            </p>
                                            {isPaidEvent && (
                                                <p className="text-sm text-v3-R400">
                                                    Paid events cannot require
                                                    attendee approval. Reset the
                                                    ticket price to enable this
                                                    feature.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* <If
                            condition={
                                !!event.membershipChapterSelectOptions?.length
                            }
                        >
                            <FormField
                                control={form.control}
                                name="membershipTierId"
                                render={({ field }) => (
                                    <FormItem className="flex w-full flex-col gap-2">
                                        <FormLabel className="text-sm font-[300] text-v3-N300">
                                            Chapter
                                        </FormLabel>
                                        <FormControl>
                                            <Select
                                                onValueChange={field.onChange}
                                                defaultValue={
                                                    field.value ?? "No Chapter"
                                                }
                                            >
                                                <FormControl>
                                                    <SelectTrigger
                                                        className={cn(
                                                            "rounded-[10px] p-2 focus:ring-0 focus:ring-offset-0",
                                                            "hover:border hover:border-v3-clover focus:border focus:border-v3-clover focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
                                                        )}
                                                    >
                                                        <SelectValue placeholder="Select a chapter" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent position="item-aligned">
                                                    {allChapterOptions.map(
                                                        (chapter) => (
                                                            <SelectItem
                                                                key={chapter.id}
                                                                value={
                                                                    chapter.id
                                                                }
                                                            >
                                                                {chapter.name}
                                                            </SelectItem>
                                                        ),
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </If> */}

                        <If condition={!!isAllowedToEditStatus}>
                            <FormField
                                control={form.control}
                                name="eventJourneyStatus"
                                render={({ field }) => (
                                    <FormItem className="flex w-full flex-col gap-2">
                                        <FormLabel
                                            className={cn(
                                                "text-sm font-[300] text-v3-N300",
                                                customization?.inputLabelClassName,
                                            )}
                                        >
                                            Event Status
                                        </FormLabel>
                                        <FormControl>
                                            <Select
                                                onValueChange={field.onChange}
                                                defaultValue={field.value}
                                                disabled={isStatusDisabled}
                                            >
                                                <FormControl>
                                                    <SelectTrigger
                                                        className={cn(
                                                            "rounded-[10px] p-2 focus:ring-0 focus:ring-offset-0",
                                                            "hover:border hover:border-v3-clover focus:border focus:border-v3-clover focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
                                                            customization?.inputClassName,
                                                        )}
                                                    >
                                                        <SelectValue placeholder="Select a status" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent
                                                    position="item-aligned"
                                                    className={
                                                        customization
                                                            ?.selectInput
                                                            ?.listClassName
                                                    }
                                                >
                                                    {eventStatuses.map(
                                                        (status) => (
                                                            <SelectItem
                                                                key={status}
                                                                value={status}
                                                            >
                                                                <span>
                                                                    {toTitleCase(
                                                                        status,
                                                                    )}
                                                                </span>
                                                            </SelectItem>
                                                        ),
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </If>

                        <div className="flex flex-col gap-2">
                            <Label
                                className={cn(
                                    "text-sm font-[300] text-v3-N300",
                                    customization?.inputLabelClassName,
                                )}
                            >
                                Start
                            </Label>

                            <div className="flex w-full items-center gap-1">
                                <FormField
                                    control={form.control}
                                    name="startDate.date"
                                    render={({ field }) => (
                                        <FormItem className="flex-1 space-y-2">
                                            <FormControl>
                                                <DatePicker
                                                    value={field.value}
                                                    onChange={(date) => {
                                                        field.onChange(date);
                                                        handleDateChange();
                                                    }}
                                                    disabled={{
                                                        before: new Date(),
                                                    }}
                                                    className="rounded-r-none"
                                                    theme={
                                                        customization?.defaultTheme
                                                    }
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="startDate.time"
                                    render={({ field }) => (
                                        <FormItem className="flex-1 space-y-2">
                                            <FormControl>
                                                <TimePicker
                                                    value={field.value}
                                                    onChange={(time) => {
                                                        field.onChange(time);
                                                        handleDateChange();
                                                    }}
                                                    className="rounded-l-none"
                                                    theme={
                                                        customization?.defaultTheme
                                                    }
                                                ></TimePicker>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col gap-2">
                            <Label
                                className={cn(
                                    "text-sm font-[300] text-v3-N300",
                                    customization?.inputLabelClassName,
                                )}
                            >
                                End
                            </Label>

                            <div className="flex w-full items-center gap-1">
                                <FormField
                                    control={form.control}
                                    name="endDate.date"
                                    render={({ field }) => (
                                        <FormItem className="flex-1 space-y-2">
                                            <FormControl>
                                                <DatePicker
                                                    value={field.value}
                                                    onChange={(date) => {
                                                        field.onChange(date);
                                                        handleDateChange();
                                                    }}
                                                    disabled={{
                                                        before:
                                                            startDate.date ??
                                                            new Date(),
                                                    }}
                                                    className="rounded-r-none"
                                                    theme={
                                                        customization?.defaultTheme
                                                    }
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="endDate.time"
                                    render={({ field }) => (
                                        <FormItem className="flex-1 space-y-2">
                                            <FormControl>
                                                <TimePicker
                                                    value={field.value}
                                                    onChange={(time) => {
                                                        field.onChange(time);
                                                        handleDateChange();
                                                    }}
                                                    dateToCompare={getStartDateTime()}
                                                    selectedDate={endDate.date}
                                                    className="rounded-l-none"
                                                    theme={
                                                        customization?.defaultTheme
                                                    }
                                                ></TimePicker>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>

                        <FormField
                            control={form.control}
                            name="isTimeSet"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl className="flex items-center">
                                        <div className="flex items-center gap-2">
                                            <Switch
                                                checked={!field.value}
                                                className="h-[18px] w-[34px] data-[state=checked]:bg-v3-clover"
                                                thumbClassName="h-3 w-3 data-[state=checked]:translate-x-4 bg-white"
                                                onCheckedChange={(checked) => {
                                                    field.onChange(!checked);
                                                }}
                                            />
                                            <FormLabel>
                                                Hide date & time
                                            </FormLabel>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="location"
                            render={({ field }) => (
                                <FormItem className="flex w-full flex-col gap-2">
                                    <FormLabel
                                        className={cn(
                                            "text-sm font-[300] text-v3-N300",
                                            customization?.inputLabelClassName,
                                        )}
                                    >
                                        Location
                                    </FormLabel>
                                    <FormControl>
                                        <LocationSelector
                                            value={field.value}
                                            customization={
                                                customization?.selectInput
                                            }
                                            onLocationSelect={(location) => {
                                                field.onChange(location);
                                                updateTimezoneFromLocation(
                                                    location,
                                                );

                                                if (!location.name) {
                                                    handleLocationDelete();
                                                }
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {location &&
                            location.type === "physical" &&
                            location.name && (
                                <FormField
                                    control={form.control}
                                    name="isLocationHidden"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl className="flex items-center">
                                                <div className="flex items-center gap-2">
                                                    <Switch
                                                        checked={field.value}
                                                        className="h-[18px] w-[34px] data-[state=checked]:bg-v3-clover"
                                                        thumbClassName="h-3 w-3 data-[state=checked]:translate-x-4 bg-white"
                                                        onCheckedChange={(
                                                            checked,
                                                        ) => {
                                                            field.onChange(
                                                                !!checked,
                                                            );
                                                            form.setValue(
                                                                "timeout",
                                                                0,
                                                            );
                                                            form.setValue(
                                                                "pseudoLocation",
                                                                "",
                                                            );
                                                        }}
                                                    />
                                                    <FormLabel>
                                                        Hide exact location
                                                    </FormLabel>
                                                </div>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}

                        {isLocationHidden && (
                            <div className="flex flex-col gap-2">
                                <FormField
                                    control={form.control}
                                    name="timeout"
                                    render={({ field }) => (
                                        <FormItem className="">
                                            <FormLabel
                                                className={cn(
                                                    "text-sm font-[300] text-v3-N300",
                                                    customization?.descriptionClassName,
                                                )}
                                            >
                                                Disclose Hours before event
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="number"
                                                    value={field.value ?? ""}
                                                    onChange={(e) =>
                                                        handleProtectedTimeoutChange(
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="pseudoLocation"
                                    render={({ field }) => (
                                        <FormItem className="">
                                            <FormLabel
                                                className={cn(
                                                    "text-sm font-[300] text-v3-N300",
                                                    customization?.descriptionClassName,
                                                )}
                                            >
                                                Approximate Location (optional)
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    placeholder="Enter a general area (e.g., 'Downtown Seattle')"
                                                    value={field.value ?? ""}
                                                    onChange={(e) =>
                                                        field.onChange(
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        )}

                        <FormField
                            control={form.control}
                            name="timezone"
                            render={({ field }) => (
                                <FormItem className="flex w-full flex-col gap-2">
                                    <FormLabel
                                        className={cn(
                                            "text-sm font-[300] text-v3-N300",
                                            customization?.inputLabelClassName,
                                        )}
                                    >
                                        Timezone
                                    </FormLabel>
                                    <FormControl>
                                        <TimezoneSelect
                                            value={field.value}
                                            onChange={field.onChange}
                                            className="rounded-[0.625rem] tracking-wide"
                                            customization={
                                                customization?.selectInput
                                            }
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />

                        <div className="flex flex-col gap-2">
                            <Label
                                className={cn(
                                    "text-sm font-[300] text-v3-N300",
                                    customization?.inputLabelClassName,
                                )}
                            >
                                Capacity
                            </Label>

                            <FormField
                                control={form.control}
                                name="capacity"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <CapacityInput
                                                className="h-10 w-full cursor-pointer rounded-[0.625rem] tracking-wide"
                                                value={field.value}
                                                onChange={field.onChange}
                                                suffixComponent={
                                                    <LuPencil className="h-4 w-4 text-v3-N300" />
                                                }
                                                customization={
                                                    customization?.selectInput
                                                }
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className="flex flex-col gap-5">
                            <FormField
                                control={form.control}
                                name="groupChatUrl"
                                render={({ field }) => (
                                    <FormItem className="flex w-full flex-col gap-1">
                                        <FormLabel
                                            className={cn(
                                                "text-sm font-[300] text-v3-N300",
                                                customization?.inputLabelClassName,
                                            )}
                                        >
                                            Group Chat URL (optional)
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                className={cn(
                                                    "rounded-[0.625rem] tracking-wide",
                                                    customization?.inputClassName,
                                                )}
                                                placeholder="https://whatsapp.com/..."
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </div>

                        <FormField
                            control={form.control}
                            name="tags"
                            render={({ field }) => (
                                <FormItem className="flex flex-col gap-2">
                                    <FormLabel
                                        className={cn(
                                            "text-sm font-[300] text-v3-N300",
                                            customization?.inputLabelClassName,
                                        )}
                                    >
                                        Tags
                                    </FormLabel>
                                    <FormControl>
                                        <TagsInput
                                            value={field.value}
                                            onChange={field.onChange}
                                            suggestions={[`${event.name}`]}
                                            placeholder="Add tags..."
                                            emptyMessage="No matches found."
                                            disabled={isLoading}
                                            className="rounded-[0.625rem]"
                                            customization={
                                                customization?.selectInput
                                            }
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />

                        <If
                            condition={
                                isAllowedToEditAdvancedProperties ?? false
                            }
                        >
                            <FormField
                                control={form.control}
                                name="resourceTags"
                                render={({ field }) => (
                                    <FormItem className="flex flex-col gap-2">
                                        <FormLabel
                                            className={cn(
                                                "text-sm font-[300] text-v3-N300",
                                                customization?.inputLabelClassName,
                                            )}
                                        >
                                            Resource Tags
                                        </FormLabel>
                                        <FormControl>
                                            <TagsInput
                                                value={field.value ?? []}
                                                onChange={field.onChange}
                                                suggestions={[`${event.name}`]}
                                                placeholder="Add tags..."
                                                emptyMessage="No matches found."
                                                disabled={isLoading}
                                                className="rounded-[0.625rem]"
                                                customization={
                                                    customization?.selectInput
                                                }
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </If>

                        <If
                            condition={
                                !!isAllowedToEditStatus && !!event.community
                            }
                        >
                            <FormField
                                control={form.control}
                                name="showNotes"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel
                                            className={cn(
                                                "text-sm font-[300] text-v3-N300",
                                                customization?.inputLabelClassName,
                                            )}
                                        >
                                            Notes for host
                                        </FormLabel>
                                        <FormControl>
                                            <TextArea
                                                {...field}
                                                className={cn(
                                                    "min-h-40 w-full rounded-[0.626rem]",
                                                    customization?.inputClassName,
                                                )}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="commManagerNote"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel
                                            className={cn(
                                                "text-sm font-[300] text-v3-N300",
                                                customization?.inputLabelClassName,
                                            )}
                                        >
                                            Notes for comm manager
                                        </FormLabel>
                                        <FormControl>
                                            <TextArea
                                                {...field}
                                                className={cn(
                                                    "min-h-40 w-full rounded-[0.626rem]",
                                                    customization?.inputClassName,
                                                )}
                                            />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />
                        </If>

                        <If condition={permissions.isAdmin ?? false}>
                            <FormField
                                control={form.control}
                                name="isMatchMakerEvent"
                                render={({ field }) => (
                                    <FormItem className="flex w-full flex-col gap-2">
                                        <div className="flex items-center gap-4">
                                            <FormControl>
                                                <Checkbox
                                                    checked={field.value}
                                                    onCheckedChange={
                                                        field.onChange
                                                    }
                                                    disabled={
                                                        !permissions.isAdmin
                                                    }
                                                />
                                            </FormControl>
                                            <div className="flex flex-col gap-1">
                                                <FormLabel className="text-sm font-[300] text-v3-N300">
                                                    Is Match Maker Event
                                                </FormLabel>

                                                <p className="text-sm text-v3-N400">
                                                    Make this a match maker
                                                    event
                                                </p>
                                                {event?.priceDollars &&
                                                    event.priceDollars > 0 && (
                                                        <p className="text-sm text-v3-R400">
                                                            Paid events cannot
                                                            require attendee
                                                            approval. Reset the
                                                            ticket price to
                                                            enable this feature.
                                                        </p>
                                                    )}
                                            </div>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </If>

                        <SheetFooter
                            className={cn(
                                "sticky bottom-[-25px] left-0 right-0 justify-start rounded-t-[0.626rem] bg-v3-N-600 px-2 py-4",
                                customization?.defaultTheme === "light"
                                    ? "bg-white/60"
                                    : "bg-v3-N-600",
                            )}
                        >
                            <div className="flex w-full items-center justify-start">
                                {/* shadow under footer */}
                                <div
                                    className={cn(
                                        "pointer-events-none absolute inset-x-0 bottom-0 h-8 w-full",
                                        "bg-gradient-to-t mix-blend-darken",
                                        "from-v4-card-gray/80 to-v4-card-gray/0",
                                        "top-0 -translate-y-1/2",
                                        customization?.defaultTheme === "light"
                                            ? "from-white/30 to-v4-card-gray/0"
                                            : "from-v4-card-gray/80 to-v4-card-gray/0",
                                    )}
                                />
                                <Button
                                    variant="secondary"
                                    type="submit"
                                    disabled={isLoading}
                                    className={cn(
                                        "flex items-center gap-2",
                                        customization?.buttons?.default,
                                    )}
                                >
                                    {isLoading && (
                                        <LuLoader2 className="h-4 w-4 animate-spin" />
                                    )}
                                    {isLoading ? "Updating..." : "Update Event"}
                                </Button>
                            </div>
                        </SheetFooter>

                        <EventImageSelectionModal
                            className="z-[1500]"
                            overlayClassName="z-[1500]"
                            eventId={event.id}
                            isOpen={isImageModalOpen}
                            setIsOpen={setIsImageModalOpen}
                            setIsUploading={setIsLoading}
                        />
                    </form>
                </Form>
            </SheetContent>
        </Sheet>
    );
};
